/**
 * BEATS (surfaces.md §1.1, §1.1.2): the Move's explanation, one step at a
 * time, on the explanation's **own numbers**. A beat is a state of one
 * drawing with its sentences; the question stays at the top of the screen.
 *
 * `→`, Space or Enter goes on, `←` goes back. Every beat rendered is an
 * `explanation.beat` event, going back included; its dwell is the gap to the
 * next one. A step that is her turn on the drawing (`child`) points the hand
 * at the press and never gates: she may go on without it. A step with an
 * `ask` takes a pick (a digit by code, or a click) as `explanation.answered`,
 * and shows no verdict: the key never left the server.
 */
import { useEffect, useRef, useState } from "react";

import type { ChildExplanation } from "./api";
import { Rich } from "../lib/math";
import { drawingFor } from "../drawings/registry";
import { digitOfCode } from "./surfaces/keys";
import { nextBeat, prevBeat } from "./flow/practice";
import { useChild, useKeys } from "./ctx";

interface Props {
  explanation: ChildExplanation;
  opened: string;
  /** The beats are spent (or „Toliau" past the last one). */
  onDone: (lastBeat: number) => void;
  /** Esc, where the caller offers a way back (the review's list). */
  onEscape?: () => void;
}

export function Explain({ explanation: x, opened, onDone, onEscape }: Props) {
  const ctx = useChild();
  const [pos, setPos] = useState({ beat: 0, step: 0 });
  const [picks, setPicks] = useState<Record<string, string>>({});
  const lastBeatAt = useRef<number>(performance.now());
  const shownBeat = useRef<number>(-1);
  const drawing = drawingFor(x.drawing);
  const beat = x.beats[pos.beat];

  // One explanation.beat per beat render, back and forth.
  const renderBeat = (b: number, dir: "next" | "back") => {
    if (b === shownBeat.current) return;
    const now = performance.now();
    ctx.emit("explanation.beat", {
      opened,
      beat: b,
      of: x.beats.length,
      dir,
      dwell_prev_ms: shownBeat.current < 0 ? 0 : Math.round(now - lastBeatAt.current),
    });
    shownBeat.current = b;
    lastBeatAt.current = now;
  };

  useEffect(() => {
    renderBeat(0, "next");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const forward = () => {
    const n = nextBeat(x, pos.beat, pos.step);
    if (!n) {
      onDone(pos.beat);
      return;
    }
    if (n.beat !== pos.beat) renderBeat(n.beat, "next");
    setPos(n);
  };
  const back = () => {
    const p = prevBeat(x, pos.beat, pos.step);
    if (p.beat !== pos.beat) renderBeat(p.beat, "back");
    setPos(p);
  };

  const step = beat?.steps[pos.step];
  const ask = step?.ask ?? null;
  const pickKey = `${pos.beat}.${pos.step}`;
  const pick = (id: string, code: string, key: string) => {
    if (picks[pickKey] === id) return;
    setPicks((p) => ({ ...p, [pickKey]: id }));
    ctx.emit("explanation.answered", { opened, beat: pos.beat, step: pos.step, picked: id, trace: [[0, code, key, "ask", "pick", id]] });
  };

  const keys: Record<string, (e: KeyboardEvent) => void> = {
    ArrowRight: forward,
    " ": forward,
    Enter: forward,
    ArrowLeft: back,
  };
  if (onEscape) keys.Escape = onEscape;
  useKeys(keys);
  useEffect(() => {
    if (!ask) return;
    const h = (e: KeyboardEvent) => {
      const d = digitOfCode(e.code);
      if (d === null || e.repeat) return;
      const o = ask.options[Number(d) - 1];
      if (!o) return;
      e.preventDefault();
      pick(o.id, e.code, e.key);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  });

  const root = useRef<HTMLElement>(null);
  useEffect(() => {
    root.current?.scrollIntoView({ block: "nearest", behavior: "auto" });
  }, [pos.beat]);

  if (!beat) return null;
  return (
    <section ref={root} className={`explain ${drawing ? "with-drawing" : ""}`} aria-label="Paaiškinimas" data-phase="BEATS" data-beat={pos.beat}>
      {drawing && (
        <div className="explain-drawing">
          <drawing.Beat
            key={pos.beat}
            focus={beat.focus}
            target={(step?.child?.busena as Record<string, unknown> | undefined) ?? null}
            onTouch={() => undefined}
          />
        </div>
      )}
      <div className="explain-words">
        {beat.steps.slice(0, pos.step + 1).map((s, i) => (
          <p key={i} className={`explain-say ${i < pos.step ? "past" : ""}`}>
            <Rich text={s.say} />
          </p>
        ))}
        {ask && (
          <div className="ask" role="group" aria-label="Pasirink">
            {ask.options.map((o, i) => (
              <button
                key={o.id}
                type="button"
                className={`option ${picks[pickKey] === o.id ? "on" : ""}`}
                onClick={() => pick(o.id, "Mouse0", `opt:${o.id}`)}
              >
                <span className="num">{i + 1}</span>
                <Rich text={o.label} />
              </button>
            ))}
          </div>
        )}
        <div className="explain-nav">
          <button type="button" className="btn btn-quiet btn-small" onClick={back} disabled={pos.beat === 0 && pos.step === 0}>
            ← Atgal
          </button>
          <button type="button" className="btn btn-solid btn-small" onClick={forward} data-act="beat-next">
            Toliau <span className="key-hint">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}
